import { Check, ChevronDown, Search, X } from "lucide-react";
import { useMemo, useState, type JSX } from "react";

import { cn } from "../../lib/cn.js";

import { Button } from "./button.js";
import { Input } from "./input.js";
import {
  filterMultiSelectOptions,
  getMultiSelectSummary,
  toggleMultiSelectValue,
  type MultiSelectOption,
} from "./multi-select.helpers.js";
import { Popover, PopoverContent, PopoverTrigger } from "./popover.js";

interface MultiSelectProps {
  id?: string;
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  maxVisibleLabels?: number;
}

export function MultiSelect({
  id,
  options,
  value,
  onValueChange,
  placeholder = "Select options",
  searchPlaceholder = "Search options",
  emptyMessage = "No options found.",
  disabled = false,
  className,
  maxVisibleLabels = 2,
}: MultiSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filteredOptions = useMemo(
    () => filterMultiSelectOptions(options, query),
    [options, query],
  );
  const summary = getMultiSelectSummary(options, value, placeholder, maxVisibleLabels);
  const selectedCount = value.length;

  function toggleValue(option: MultiSelectOption): void {
    if (option.disabled) return;
    onValueChange(toggleMultiSelectValue(value, option.value));
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={id ? `${id}-listbox` : undefined}
          disabled={disabled}
          className={cn(
            "min-h-10 w-full justify-between px-3 py-2 text-left font-normal",
            // Match the neutral look of <Select> triggers instead of the
            // primary-tinted "outline" Button (gray border, subtle focus ring).
            "border-input bg-background text-foreground",
            "hover:border-input hover:bg-background hover:text-foreground",
            "focus-visible:ring-ring/30",
            className,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate",
              selectedCount === 0 && "text-muted-foreground",
            )}
          >
            {summary}
          </span>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-72 max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="border-b border-border/70 p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder={searchPlaceholder}
              className="h-9 pl-8"
            />
          </div>
          <div className="mt-2 flex min-h-6 items-center justify-between gap-2 px-1 text-xs text-muted-foreground">
            <span>{selectedCount > 0 ? `${selectedCount} selected` : placeholder}</span>
            {selectedCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => {
                  onValueChange([]);
                }}
              >
                <X className="size-3" aria-hidden="true" />
                Clear
              </Button>
            )}
          </div>
        </div>
        <div
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          aria-multiselectable="true"
          className="max-h-72 overflow-y-auto p-1"
        >
          {filteredOptions.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </p>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  onClick={() => {
                    toggleValue(option);
                  }}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm outline-none transition-colors",
                    "hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                    option.disabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <span
                    className="flex min-w-0 flex-1 items-start gap-2"
                    style={{ paddingLeft: `${(option.depth ?? 0) * 16}px` }}
                  >
                    <span
                      className={cn(
                        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border border-input bg-background",
                        isSelected && "border-primary bg-primary text-primary-foreground",
                      )}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="size-3" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{option.label}</span>
                      {option.description && (
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export type { MultiSelectOption };
